import React, { useCallback, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useLang } from "../Utility/Languagecontext";
import { COMPANY } from "../Data/Company";

const EASE = [0.76, 0, 0.24, 1];
const SPRING = { type: "spring", stiffness: 210, damping: 26 };

// وضع كل كارت في الكومة: 0 = قدّام، 1 و2 = وراه، الباقي مستخبي
const POS = [
  { y: 0, scale: 1, rotate: 0, opacity: 1 },
  { y: 28, scale: 0.94, rotate: -2.5, opacity: 1 },
  { y: 54, scale: 0.88, rotate: 2.5, opacity: 0.95 },
];
const HIDDEN = { y: 70, scale: 0.82, rotate: 0, opacity: 0 };

// سطر العنوان بيطلع من ورا قناع (المراقبة على العنوان الأب مش على المقصوص)
const MaskLine = ({ children, delay = 0, show }) => (
  <span className="mask">
    <motion.span
      className="mask__inner"
      initial={false}
      animate={{ y: show ? 0 : "115%" }}
      transition={{ duration: 1.1, delay: show ? delay : 0, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

// كل كلمة بتنور مع السكرول (وبتطفي لو رجعت لفوق): نفس تأثير فقرة About
const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.16, 1]);
  return (
    <motion.span className="word" style={{ opacity }}>
      {children}
    </motion.span>
  );
};

// نص بيتكتب كلمة كلمة بحسب السكرول
const WordsScroll = ({ text, as: Tag = "p", className, offset = ["start 0.88", "end 0.6"] }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset });
  const words = text.split(" ");
  return (
    <Tag ref={ref} className={className}>
      {words.map((w, i) => (
        <React.Fragment key={`${w}-${i}`}>
          <Word progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]}>
            {w}
          </Word>{" "}
        </React.Fragment>
      ))}
    </Tag>
  );
};

// نص بيتكتب كلمة كلمة لما on تبقى true، ويرجع باهت لما تبقى false.
// بنستخدمه لاقتباس الكارت اللي قدّام: كل ما كارت جديد يطلع قدّام اقتباسه بيتكتب من الأول.
const WordsOn = ({ text, on, as: Tag = "p", className, delay = 0, step = 0.035 }) => {
  const words = text.split(" ");
  return (
    <Tag className={className}>
      {words.map((w, i) => (
        <React.Fragment key={`${w}-${i}`}>
          <motion.span
            className="word"
            initial={false}
            animate={{ opacity: on ? 1 : 0.16 }}
            transition={{ duration: 0.5, delay: on ? delay + i * step : 0 }}
          >
            {w}
          </motion.span>{" "}
        </React.Fragment>
      ))}
    </Tag>
  );
};

const ArrowIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 10h12M11 5l5 5-5 5" />
  </svg>
);

export const MainTestominials = () => {
  const { t, isRTL, locale } = useLang();
  const tm = t.testimonials;
  // آراء حقيقية من جوجل (النص بس، من غير أسماء)
  const items = tm.items.map((quote) => ({ quote }));
  const n = items.length;
  const dir = isRTL ? -1 : 1;
  const num = (v) => v.toLocaleString(locale, { minimumIntegerDigits: 2, useGrouping: false });

  // order[0] هو الكارت اللي قدّام
  const [order, setOrder] = useState(() => items.map((_, i) => i));
  const [hover, setHover] = useState(false);
  const [dragging, setDragging] = useState(false);

  const textRef = useRef(null);
  const textIn = useInView(textRef, { amount: 0.3 });
  const stackRef = useRef(null);
  const stackIn = useInView(stackRef, { amount: 0.3 });

  const next = useCallback(() => setOrder((o) => [...o.slice(1), o[0]]), []);
  const prev = useCallback(() => setOrder((o) => [o[o.length - 1], ...o.slice(0, -1)]), []);

  const target = (pos) => {
    const c = pos < POS.length ? POS[pos] : HIDDEN;
    return { x: 0, y: c.y, scale: c.scale, rotate: c.rotate * dir, opacity: c.opacity };
  };

  const paused = hover || dragging;

  return (
    <section
      className={`testi ${paused ? "is-paused" : ""}`}
      id="testimonials"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="testi__grid">
        <div className="testi__text" ref={textRef}>
          <p className="testi__label">{t.testimonials.label}</p>
          <h2 className="testi__title">
            {t.testimonials.title.map((line, i) => (
              <MaskLine key={line} delay={i * 0.14} show={textIn}>
                {line}
              </MaskLine>
            ))}
          </h2>
          <WordsScroll as="p" className="testi__sub" text={tm.sub} offset={["start 0.95", "end 0.7"]} />
          <a className="testi__more" href={COMPANY.mapUrl} target="_blank" rel="noopener noreferrer">
            <span className="testi__stars" aria-hidden="true">★★★★★</span>
            {tm.more}
          </a>
        </div>

        <motion.div
          ref={stackRef}
          className="testi__stack"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ duration: 1, ease: EASE }}
        >
          {items.map((it, i) => {
            const pos = order.indexOf(i);
            const front = pos === 0;
            return (
              <motion.figure
                key={i}
                className={`tcard ${front ? "is-front" : "is-back"}`}
                style={{ zIndex: n - pos, pointerEvents: front ? "auto" : "none", transformOrigin: "50% 100%" }}
                initial={false}
                animate={target(pos)}
                transition={SPRING}
                drag={front ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.6}
                onDragStart={() => setDragging(true)}
                onDragEnd={(e, info) => {
                  setDragging(false);
                  if (Math.abs(info.offset.x) > 110 || Math.abs(info.velocity.x) > 600) next();
                }}
                aria-hidden={!front}
              >
                <span className="tcard__mark" aria-hidden="true">
                  “
                </span>
                <WordsOn as="blockquote" className="tcard__quote" text={it.quote} on={front && stackIn} delay={0.25} step={0.03} />
                <figcaption className="tcard__who">
                  <span className="tcard__avatar" aria-hidden="true">
                    G
                  </span>
                  <span className="tcard__id">
                    <span className="tcard__name">{tm.source}</span>
                    <span className="tcard__role">
                      {tm.role}
                      {tm.translated ? ` · ${tm.translated}` : ""}
                    </span>
                  </span>
                  <span className="tcard__tag" aria-label="5/5">
                    ★★★★★
                  </span>
                </figcaption>
              </motion.figure>
            );
          })}
        </motion.div>

        <div className="testi__controls">
          <span className="testi__count">
            {num(order[0] + 1)} / {num(n)}
          </span>
          {/* شريط التقدّم بيحدد وقت التبديل التلقائي، وبيقف لما تقف بالماوس أو تسحب */}
          <span className="testi__prog" aria-hidden="true">
            <i key={order[0]} className="testi__fill" onAnimationEnd={next} />
          </span>
          <div className="testi__btns">
            <button type="button" className="tbtn tbtn--prev" onClick={prev} aria-label={t.testimonials.prev}>
              <ArrowIcon />
            </button>
            <button type="button" className="tbtn tbtn--next" onClick={next} aria-label={t.testimonials.next}>
              <ArrowIcon />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};